import React, {useEffect, useRef, useState} from 'react';
import {useSwipeable} from 'react-swipeable';

import {Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack} from '@mui/material';

import Icons from 'assets/icons';
import {useCanvasSave} from 'canvas/useCanvas';
import {useCanvasStore} from 'canvas/zustand';
import {Text} from 'components';
import {useGroup} from 'module/group/useGroup';
import {SheetDialog} from 'module/sheet';
import {useSheet} from 'module/sheet/useSheet';
import {useLicenseManager} from 'module/user';
import {usePrefsStore, useSnackbarStore} from 'store';

const styles = {
    root: {
        height: '100%',
    },
    stepper: {
        backgroundColor: 'black.light',
        position: 'static',
        width: '100%',
        color: 'white.main',
        paddingTop: 3,
        alignItems: 'flex-start',
    },
    dots: {
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1,
        justifyContent: 'center',
    },
    dot: {
        borderRadius: '50%',
        width: 16,
        height: 16,
        mx: 1,
        cursor: 'pointer',
        backgroundColor: theme => theme.palette.grey.lightester,
        WebkitTapHighlightColor: 'transparent',
    },
    dotActive: {
        backgroundColor: theme => theme.palette.red.highlight,
    },
    button: {
        height: '100%',
        alignItems: 'flex-start',
        '& svg': {
            width: 20,
            height: 20,
            fill: theme => theme.palette.white.main,
        },
        '& .Mui-disabled svg': {
            fill: theme => theme.palette.grey.darker,
        }
    },
};

const Footer = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [pendingStep, setPendingStep] = useState(null);

    const {selected: groupId} = useGroup();
    const {sheets, setSelected, selected, selectedSheet} = useSheet(groupId);
    const {hasAccess} = useLicenseManager();
    const [showSheetDialog, setShowSheetDialog] = useState(false);

    const dirty = useCanvasStore((state) => state.dirty);
    const {save: saveCanvas} = useCanvasSave();
    const background = usePrefsStore((state) => state.background);
    const showInfo = useSnackbarStore((state) => state.show);

    const sheetsRef = useRef(sheets);

    const doSwitch = (step) => {
        setActiveStep(step);
    };

    const requestSwitch = (step) => {
        if (dirty) {
            setPendingStep(step);
        } else {
            doSwitch(step);
        }
    };

    const handleNext = () => {
        const next = activeStep >= sheets.length - 1 ? activeStep : activeStep + 1;
        if (next !== activeStep) requestSwitch(next);
    };

    const handleBack = () => {
        const next = activeStep === 0 ? activeStep : activeStep - 1;
        if (next !== activeStep) requestSwitch(next);
    };

    const handleSaveAndSwitch = async () => {
        try {
            await saveCanvas(selectedSheet, {background});
            showInfo('board.sheet.action.save_success');
        } catch (e) {
            showInfo(e.message, {severity: 'warning'});
        }

        doSwitch(pendingStep);
        setPendingStep(null);
    };

    const handleDiscardAndSwitch = () => {
        doSwitch(pendingStep);
        setPendingStep(null);
    };

    const handleCancelSwitch = () => {
        setPendingStep(null);
    };

    const canAddSheet = hasAccess('group-sheets')?.limit?.max > sheets.length;

    const handleAdd = () => {
        setShowSheetDialog(true);
    };

    const doSwipe = (e) => {
        if (e.dir === 'Left') {
            handleNext();
        }

        if (e.dir === 'Right') {
            handleBack();
        }
    };

    const swipeHandlers = useSwipeable({
        onSwiped: doSwipe,
        trackMouse: true,
    });

    useEffect(() => {
        sheetsRef.current = sheets;
    }, [sheets]);

    useEffect(() => {
        setSelected(sheetsRef.current[activeStep]?.id);
    }, [activeStep, setSelected]);

    // watch for external changes (add/remove/sort)
    useEffect(() => {
        for (let i = 0; i < sheets.length; i++) {
            if (sheets[i].id === selected) {
                setActiveStep(i);
            }
        }
    }, [selected, sheets]);

    return <Stack sx={styles.root} direction="row" flexGrow={1}>
        {showSheetDialog && <SheetDialog open={showSheetDialog} onClose={() => setShowSheetDialog(false)} onSave={() => setShowSheetDialog(false)}/>}
        {sheets.length > 1 && <Stack
            sx={styles.stepper}
            direction="row"
            alignItems="flex-start"
            width="100%"
            {...swipeHandlers}
        >
            <Button
                sx={styles.button}
                color="white"
                onClick={handleBack}
                disableRipple
                disabled={activeStep === 0}
                style={{visibility: activeStep === 0 ? 'hidden' : 'visible'}}>
                <Icons.ArrowLeft/>
            </Button>
            <Box sx={styles.dots}>
                {sheets.length <= 7
                    ? sheets.map((_, i) => (
                        <Box
                            key={i}
                            onClick={() => requestSwitch(i)}
                            sx={[styles.dot, i === activeStep && styles.dotActive]}
                        />
                    ))
                    : <Box sx={{color: 'white.main'}}>{activeStep + 1} / {sheets.length}</Box>
                }
            </Box>
            <Button
                sx={styles.button}
                color="white"
                onClick={activeStep >= sheets.length - 1 && canAddSheet ? handleAdd : handleNext}
                disableRipple
                disabled={activeStep >= sheets.length - 1 && !canAddSheet}
                style={{visibility: activeStep >= sheets.length - 1 && !canAddSheet ? 'hidden' : 'visible'}}>
                {activeStep >= sheets.length - 1 && canAddSheet ? <Icons.New/> : <Icons.ArrowRight/>}
            </Button>
        </Stack>}

        <Dialog open={pendingStep !== null} onClose={handleCancelSwitch} maxWidth="xs" fullWidth>
            <DialogTitle sx={{color: 'white.main', fontSize: '18px', backgroundColor: 'grey.darker'}}>
                <Text>board.sheet.messages.not_saved</Text>
            </DialogTitle>
            <DialogContent sx={{backgroundColor: 'grey.darker'}}>
                <DialogContentText>
                    <Text>board.sheet.messages.not_saved_switch</Text>
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{padding: '0 24px 24px', backgroundColor: 'grey.darker'}}>
                <Button onClick={handleCancelSwitch} color="secondary" variant="contained">
                    <Text>ui.actions.cancel</Text>
                </Button>
                <Button onClick={handleDiscardAndSwitch} color="warning" variant="contained">
                    <Text>ui.actions.discard</Text>
                </Button>
                <Button onClick={handleSaveAndSwitch} color="error" variant="contained">
                    <Text>ui.actions.save</Text>
                </Button>
            </DialogActions>
        </Dialog>
    </Stack>;
};

export default Footer;